import { useState, useEffect } from "react";
import { Zap, LayoutGrid, Clock, Coins, CheckCircle2, Users, X, ChevronDown, ChevronUp, Settings2 } from "lucide-react";
import type { FlowGraph } from "@/lib/algo/graph";

const LAUNCH_NODE_ID = "launch";

export function PlaygroundConfigurator({
  update,
  onClose
}: {
  graph: FlowGraph;
  update: (fn: (draft: FlowGraph) => void) => void;
  onClose?: () => void;
}) {
  const [walletCount, setWalletCount] = useState(() => Number(localStorage.getItem("pg_walletCount")) || 10);
  const [delaySec, setDelaySec] = useState(() => Number(localStorage.getItem("pg_delaySec")) || 86400);
  const [baseSol, setBaseSol] = useState(() => Number(localStorage.getItem("pg_baseSol")) || 0.5);
  const [structure, setStructure] = useState<"linear" | "snipe_bundle" | "fibonacci" | "volume_pairs">(() => (localStorage.getItem("pg_structure") as any) || "linear");
  const [selectedActions, setSelectedActions] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem("pg_actions");
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    const oldAction = localStorage.getItem("pg_action");
    return oldAction ? [oldAction] : ["hold"];
  });
  const [continuousLoop, setContinuousLoop] = useState(() => localStorage.getItem("pg_loop") === "true");
  const [loopInterval, setLoopInterval] = useState(() => Number(localStorage.getItem("pg_loopInt")) || 120);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    localStorage.setItem("pg_walletCount", String(walletCount));
    localStorage.setItem("pg_delaySec", String(delaySec));
    localStorage.setItem("pg_baseSol", String(baseSol));
    localStorage.setItem("pg_structure", structure);
    localStorage.setItem("pg_actions", JSON.stringify(selectedActions));
    localStorage.setItem("pg_loop", String(continuousLoop));
    localStorage.setItem("pg_loopInt", String(loopInterval));
  }, [walletCount, delaySec, baseSol, structure, selectedActions, continuousLoop, loopInterval]);

  const handleGenerateStructure = () => {
    setIsGenerating(true);
    
    update((draft) => {
      const existingWallets = [...draft.wallets];
      
      // Clear existing graph EXCEPT launch
      draft.wallets = [];
      draft.edges = [];
      draft.sells = [];
      
      const count = Math.max(1, Math.min(50, walletCount));
      const amount = Math.max(0.01, baseSol);
      const delay = Math.max(0, delaySec);

      // 1. Create Wallets in a grid (preserving positions if they exist)
      for (let i = 0; i < count; i++) {
        const wId = `w${i + 1}`;
        const existing = existingWallets.find(w => w.id === wId);
        const row = Math.floor(i / 5);
        const col = i % 5;
        draft.wallets.push({
          id: wId,
          label: `Alpha-${i + 1}`,
          group: "core",
          fundingSol: 0,
          enabled: true,
          x: existing ? existing.x : 60 + col * 250,
          y: existing ? existing.y : 60 + row * 380,
        });
      }

      // 2. Connect Initial Edges based on structure
      let fibA = 1, fibB = 1;
      
      for (let i = 0; i < count; i++) {
        const wId = `w${i + 1}`;
        const source = i === 0 ? LAUNCH_NODE_ID : `w${i}`;
        
        let wait = delay;
        let bid = undefined;
        let buyAmount = amount;

        if (structure === "snipe_bundle") {
          const bundleSize = Math.min(4, count);
          if (i < bundleSize) {
            wait = 0;
            bid = "snipe-1";
            buyAmount = amount * 2;
          }
        } else if (structure === "fibonacci") {
          wait = delay * fibA;
          const next = fibA + fibB; fibA = fibB; fibB = next;
        }

        draft.edges.push({
          id: `e-${source}-${wId}`, source, target: wId, kind: "buy", sol: buyAmount,
          waitSec: wait, bundleId: bid, order: 0, sellPct: 100
        });

        if (structure === "volume_pairs" && i % 2 === 1) {
          const curr = draft.edges[draft.edges.length - 1];
          const prev = draft.edges[draft.edges.length - 2];
          curr.waitSec = 0;
          if (!curr.bundleId) {
             const pairBid = `pair-${Math.floor(i/2)}`;
             curr.bundleId = pairBid;
             if (prev) prev.bundleId = prev.bundleId || pairBid;
          }
        }
      }
    });

    setTimeout(() => {
      setIsGenerating(false);
    }, 400);
  };

  const handleApplyActions = () => {
    if (selectedActions.length === 0) return;
    setIsGenerating(true);
    
    update((draft) => {
      // Clear existing sells and any special edges (like ping-pong secondary buys)
      draft.sells = [];
      draft.edges = draft.edges.filter(e => !e.id.endsWith("-2"));

      const amount = Math.max(0.01, baseSol);
      const delay = Math.max(0, delaySec);

      // Apply Trading Actions to each wallet
      for (let i = 0; i < draft.wallets.length; i++) {
        const wallet = draft.wallets[i];
        const wId = wallet.id;
        
        wallet.loopIntervalMs = continuousLoop ? Math.max(10, loopInterval) * 1000 : undefined;

        const action = selectedActions[i % selectedActions.length];

        if (action === "dump") {
          draft.sells.push({
            id: `sell-${wId}`, walletId: wId, trigger: "time", value: 60 + (i * 10), pct: 100,
            x: wallet.x + 10, y: wallet.y + 110
          });
        } else if (action === "gradual_sell") {
          [25, 25, 25, 25].forEach((pct, idx) => {
            draft.sells.push({
              id: `sell-${wId}-${idx}`, walletId: wId, trigger: "time", value: 30 + (idx * 30), pct,
              x: wallet.x + 10, y: wallet.y + 110 + (idx * 55)
            });
          });
        } else if (action === "ping_pong") {
          draft.sells.push({
            id: `sell-${wId}-1`, walletId: wId, trigger: "time", value: 30, pct: 50,
            x: wallet.x - 50, y: wallet.y + 100
          });
          draft.edges.push({
            id: `e-launch-${wId}-2`, source: LAUNCH_NODE_ID, target: wId, kind: "buy", sol: amount * 0.5,
            waitSec: delay * i + 60, bundleId: undefined, order: 0, sellPct: 100
          });
          draft.sells.push({
            id: `sell-${wId}-2`, walletId: wId, trigger: "time", value: 90, pct: 100,
            x: wallet.x + 50, y: wallet.y + 100
          });
        } else if (action === "tape") {
          draft.sells.push({
            id: `sell-${wId}-1`, walletId: wId, trigger: "time", value: 30, pct: 40,
            x: wallet.x - 50, y: wallet.y + 100
          });
          draft.edges.push({
            id: `e-launch-${wId}-2`, source: LAUNCH_NODE_ID, target: wId, kind: "buy", sol: amount * 0.5,
            waitSec: delay * i + 60, bundleId: undefined, order: 0, sellPct: 100
          });
          draft.sells.push({
            id: `sell-${wId}-2`, walletId: wId, trigger: "time", value: 120, pct: 100,
            x: wallet.x + 50, y: wallet.y + 100
          });
        } else if (action === "double_top") {
          const isGroupA = i < draft.wallets.length / 2;
          if (isGroupA) {
            draft.sells.push({ id: `sell-${wId}-a`, walletId: wId, trigger: "time", value: 30, pct: 100, x: wallet.x, y: wallet.y + 110 });
          } else {
            draft.sells.push({ id: `sell-${wId}-b1`, walletId: wId, trigger: "time", value: 30, pct: 50, x: wallet.x, y: wallet.y + 110 });
            draft.edges.push({ id: `e-dt-${wId}`, source: LAUNCH_NODE_ID, target: wId, kind: "buy", sol: amount * 0.5, waitSec: delay * i + 45, bundleId: undefined, order: 0, sellPct: 100 });
            draft.sells.push({ id: `sell-${wId}-b2`, walletId: wId, trigger: "time", value: 75, pct: 100, x: wallet.x, y: wallet.y + 170 });
          }
        } else if (action === "fakeout") {
          draft.sells.push({ id: `sell-${wId}`, walletId: wId, trigger: "time", value: 5, pct: 100, x: wallet.x, y: wallet.y + 110 });
        } else if (action === "judas_swing") {
          draft.sells.push({ id: `sell-${wId}-fake`, walletId: wId, trigger: "time", value: 15, pct: 75, x: wallet.x - 50, y: wallet.y + 110 });
          draft.edges.push({ id: `e-judas-${wId}`, source: LAUNCH_NODE_ID, target: wId, kind: "buy", sol: amount * 2, waitSec: delay * i + 20, bundleId: undefined, order: 0, sellPct: 100 });
          draft.sells.push({ id: `sell-${wId}-real`, walletId: wId, trigger: "time", value: 60, pct: 100, x: wallet.x + 50, y: wallet.y + 110 });
        }
      }
    });

    setTimeout(() => {
      setIsGenerating(false);
    }, 400);
  };

  const TOPOLOGY_DESC: Record<string, string> = {
    linear: "A steady, sequential chain of buys. Each wallet waits a set delay.",
    snipe_bundle: "The first 4 wallets buy instantly in the same block as Launch.",
    fibonacci: "The delay between each wallet increases exponentially.",
    volume_pairs: "Wallets are paired together into mini-bundles (2 wallets trade at same time)."
  };
  
  const ACTION_DESC: Record<string, string> = {
    hold: "Wallets hold indefinitely (Diamond Hand).",
    dump: "All wallets sell 100% simultaneously.",
    gradual_sell: "Wallets split holdings into 4 chunks and sell gradually.",
    ping_pong: "Wallets sell 50% for profit, then buy back in before dumping.",
    tape: "Complex mix of partial sells and buys to paint the chart.",
    double_top: "Creates a double top: pump, dump, pump to exact same high, dump.",
    fakeout: "Buy heavy to create a massive green candle, then dump 5 seconds later.",
    judas_swing: "Dump early to scare weak hands, then buy double for the real pump."
  };

  return (
    <div className={`absolute top-16 right-4 ${isCollapsed ? 'bottom-auto h-auto w-auto' : 'bottom-4 w-[340px] flex flex-col'} bg-[#111]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden transition-all duration-300`}>
      
      {/* Header bar */}
      <div 
        className="flex items-center justify-between p-3 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center gap-2">
          <Settings2 className="size-4 text-emerald-500" />
          <span className="text-xs font-black uppercase tracking-widest text-white/80">Setup Playground</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-white/40 hover:text-white transition-colors">
            {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </div>
          {onClose && (
            <button 
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="p-1 text-white/40 hover:text-red-400 rounded-md hover:bg-white/10 transition-colors"
              title="Close"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>

      {!isCollapsed && (
        <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
          <div className="flex flex-col gap-4">
            
            {/* Structure Section */}
            <div className="flex flex-col gap-3 pb-4 border-b border-white/10">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-500 mb-0.5">1. Generate Structure</div>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 flex items-center gap-1.5">
                      <LayoutGrid className="size-3" /> Topology
                    </label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {[
                        { value: "linear", label: "Linear Ladder" },
                        { value: "snipe_bundle", label: "Snipe Bundle" },
                        { value: "fibonacci", label: "Fibonacci" },
                        { value: "volume_pairs", label: "Volume Pairs" }
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setStructure(opt.value as any)}
                          className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all border ${
                            structure === opt.value
                              ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-400"
                              : "bg-black/50 border-white/10 text-white/50 hover:bg-white/5 hover:text-white"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 flex items-center gap-1.5">
                        <Users className="size-3" /> Wallets
                      </label>
                      <input 
                        type="number" min="1" max="50" 
                        value={walletCount} onChange={(e) => setWalletCount(Number(e.target.value))}
                        className="bg-black/50 border border-white/10 rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/50 w-16 text-center transition-colors"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 flex items-center gap-1.5" title="The amount of SOL each wallet uses for its initial buy">
                      <Coins className="size-3" /> Init Buy
                    </label>
                    <input 
                      type="number" min="0.01" step="0.1" 
                      value={baseSol} onChange={(e) => setBaseSol(Number(e.target.value))}
                      className="bg-black/50 border border-white/10 rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/50 w-20 text-center transition-colors"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 flex items-center gap-1.5">
                      <Clock className="size-3" /> Delay(s)
                    </label>
                    <input 
                      type="number" min="0" step="1" 
                      value={delaySec} onChange={(e) => setDelaySec(Number(e.target.value))}
                      className="bg-black/50 border border-white/10 rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/50 w-16 text-center transition-colors"
                    />
                  </div>

                  <button 
                    onClick={handleGenerateStructure}
                    disabled={isGenerating}
                    className="bg-zinc-800 hover:bg-zinc-700 border border-emerald-500/30 text-emerald-400 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all disabled:opacity-50 h-[36px] w-full"
                  >
                    {isGenerating ? (
                      <><Zap className="size-4 animate-pulse" /> Build...</>
                    ) : (
                      <><LayoutGrid className="size-4" /> Build Graph</>
                    )}
                  </button>
                </div>
                
                <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg border border-white/10 w-full">
                  <span className="text-[10px] uppercase font-bold tracking-widest text-emerald-500/80 shrink-0">Topology Info:</span>
                  <span className="text-xs text-white/70">{TOPOLOGY_DESC[structure]}</span>
                </div>
              </div>
            </div>

            {/* Actions Section */}
            <div className="flex flex-col gap-3">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-500 mb-0.5">2. Apply Actions</div>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 flex items-center gap-1.5">
                    <Zap className="size-3" /> Sell Strategy
                  </label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {[
                      { value: "hold", label: "Diamond Hand" },
                      { value: "dump", label: "Pump & Dump" },
                      { value: "gradual_sell", label: "Gradual Selloff" },
                      { value: "ping_pong", label: "Ping Pong" },
                      { value: "tape", label: "Write a Tape" },
                      { value: "double_top", label: "Double Top" },
                      { value: "fakeout", label: "Fakeout (Bull Trap)" },
                      { value: "judas_swing", label: "Judas Swing" }
                    ].map(opt => {
                      const isSelected = selectedActions.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          onClick={() => {
                            setSelectedActions(prev => {
                              if (prev.includes(opt.value)) {
                                if (prev.length === 1) return prev; // Keep at least one
                                return prev.filter(v => v !== opt.value);
                              }
                              return [...prev, opt.value];
                            });
                          }}
                          className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all border ${
                            isSelected
                              ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-400"
                              : "bg-black/50 border-white/10 text-white/50 hover:bg-white/5 hover:text-white"
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col gap-3 w-full">
                  <div className="flex items-center gap-4 bg-black/30 p-2.5 rounded-lg border border-white/5">
                    <label className="flex items-center gap-2 text-xs font-bold text-white/80 cursor-pointer">
                      <input type="checkbox" checked={continuousLoop} onChange={e => setContinuousLoop(e.target.checked)} className="rounded border-white/20 bg-black/50 text-emerald-500 focus:ring-emerald-500" />
                      Continuous Loop
                    </label>
                    {continuousLoop && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase text-white/50 tracking-widest font-bold">Interval(s)</span>
                        <input type="number" min="10" step="5" value={loopInterval} onChange={e => setLoopInterval(Number(e.target.value))} className="bg-black/50 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-emerald-500/50 w-16 text-center" />
                      </div>
                    )}
                  </div>

                  <button 
                    onClick={handleApplyActions}
                    disabled={isGenerating}
                    className="bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all disabled:opacity-50 h-[36px] w-full"
                  >
                    {isGenerating ? (
                      <><Zap className="size-4 animate-pulse" /> Apply...</>
                    ) : (
                      <><CheckCircle2 className="size-4" /> Apply Actions</>
                    )}
                  </button>
                </div>
                
                <div className="flex items-center gap-2 bg-white/5 px-3 py-2 rounded-lg border border-white/10 mt-1 w-full">
                  <span className="text-[10px] uppercase font-bold tracking-widest text-emerald-500/80 shrink-0">Action Info:</span>
                  <span className="text-xs text-white/70">
                    {selectedActions.length === 1 ? ACTION_DESC[selectedActions[0]] : "Mix of multiple selected actions distributed across wallets."}
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}